import {useMemo, useState, useEffect} from 'react';
import PropTypes from 'prop-types';
import {useDebouncedEffect} from '@ozgene/gp5.hooks.use-debounced-effect';
import {TextField, Grid} from '@material-ui/core';
import {getStringChangeHandler} from '@ozgene/gp5.utils.data-functions';
import SelectTag from 'Sbs/components/SelectTag';
import SelectSbsStatus from 'Sbs/components/SelectSbsStatus';
import {validateUniqueTitle} from 'Sbs/service/SbsService';
import SelectTeam from 'Teams/components/SelectTeam';

export default function SbsEditor(props) {
  const {entity, onPropertyChange, onValidityChange} = props;
  const {id = '', title = '', teams = [], sbsStatus, tags = []} = entity;
  const [isValidating, setIsValidating] = useState(false);
  const [titleErrorText, setTitleErrorText] = useState('');
  const handleChangeTitle = getStringChangeHandler('title', onPropertyChange);
  const handleChangeSbsStatus = (sbsStatus) => onPropertyChange({sbsStatus});
  const handleChangeTeams = (teams) => onPropertyChange({teams});
  const handleChangeTags = (tags) => onPropertyChange({tags});
  const isTitleInvalid = useMemo(
    () => !title || !!titleErrorText,
    [title, titleErrorText],
  );

  useDebouncedEffect(
    () => {
      const validateTitle = async () => {
        setTitleErrorText(await validateUniqueTitle(title, id));
        setIsValidating(false);
      };

      return validateTitle();
    },
    [title],
    500,
  );

  useEffect(() => setIsValidating(true), [title]);
  useEffect(
    () =>
      onValidityChange(!isValidating && !isTitleInvalid && teams.length > 0),
    [isValidating, isTitleInvalid, onValidityChange, teams],
  );

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Title"
          type="text"
          value={title}
          onChange={handleChangeTitle}
          error={isTitleInvalid}
          helperText={titleErrorText}
          inputProps={{maxLength: 100}}
        />
      </Grid>
      <Grid item md={3} sm={4} xs={12}>
        <SelectTeam
          value={teams}
          onChange={handleChangeTeams}
          multiple
          error={teams.length === 0}
          errorText={teams.length === 0 ? 'Team is required' : ''}
        />
      </Grid>
      <Grid item md={3} sm={4} xs={12}>
        <SelectSbsStatus
          useDefault
          value={sbsStatus}
          onChange={handleChangeSbsStatus}
          label={'SBS Status'}
          required
        />
      </Grid>
      <Grid item md={6} sm={4} xs={12}>
        <SelectTag value={tags} onChange={handleChangeTags} />
      </Grid>
    </Grid>
  );
}

SbsEditor.propTypes = {
  entity: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string,
    teams: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
      }),
    ),
    sbsStatus: PropTypes.shape({
      id: PropTypes.string,
      status: PropTypes.string,
    }),
    tags: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
      }),
    ),
  }),
  onPropertyChange: PropTypes.func.isRequired,
  onValidityChange: PropTypes.func.isRequired,
};
